import React, { useState } from 'react';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Button } from '../../components/atoms/Button';
import { Icon } from '@lnr-v2/ui';
import { TaxonomyCard } from '../../components/organisms/TaxonomyCard';
import { useTaxonomies } from '../../hooks/useTaxonomies';
import { useTerms } from '../../hooks/useTerms';
import { toast } from '../../utils/toast';
import type {
  TaxonomyWithTerms,
  TermWithTaxonomy,
  CreateTaxonomyDto,
  CreateTermDto,
} from '../../hooks/useTaxonomies';
import styles from './TaxonomiesPage.module.css';

export const TaxonomiesPage: React.FC = () => {
  const {
    taxonomies,
    loading,
    createTaxonomy,
    updateTaxonomy,
    deleteTaxonomy,
  } = useTaxonomies();
  const { createTerm, updateTerm, deleteTerm } = useTerms();

  const [selectedTaxonomy, setSelectedTaxonomy] =
    useState<TaxonomyWithTerms | null>(null);
  const [showTaxonomyModal, setShowTaxonomyModal] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [editingTaxonomy, setEditingTaxonomy] =
    useState<TaxonomyWithTerms | null>(null);
  const [editingTerm, setEditingTerm] = useState<TermWithTaxonomy | null>(null);

  // Taxonomy CRUD handlers
  const handleCreateTaxonomy = () => {
    setEditingTaxonomy(null);
    setShowTaxonomyModal(true);
  };

  const handleEditTaxonomy = (taxonomy: TaxonomyWithTerms) => {
    setEditingTaxonomy(taxonomy);
    setShowTaxonomyModal(true);
  };

  const handleDeleteTaxonomy = async (taxonomyId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this taxonomy? All associated terms will also be deleted.'
      )
    ) {
      return;
    }

    try {
      await deleteTaxonomy(taxonomyId);
      toast.success('Taxonomy deleted successfully');
    } catch (error) {
      toast.error('Failed to delete taxonomy');
    }
  };

  const handleSaveTaxonomy = async (data: CreateTaxonomyDto) => {
    try {
      if (editingTaxonomy) {
        await updateTaxonomy(editingTaxonomy.id, data);
        toast.success('Taxonomy updated successfully');
      } else {
        await createTaxonomy(data);
        toast.success('Taxonomy created successfully');
      }
      setShowTaxonomyModal(false);
      setEditingTaxonomy(null);
    } catch (error) {
      toast.error(
        `Failed to ${editingTaxonomy ? 'update' : 'create'} taxonomy`
      );
    }
  };

  // Term CRUD handlers
  const handleCreateTerm = (taxonomy: TaxonomyWithTerms) => {
    setSelectedTaxonomy(taxonomy);
    setEditingTerm(null);
    setShowTermModal(true);
  };

  const handleEditTerm = (
    term: TermWithTaxonomy,
    taxonomy: TaxonomyWithTerms
  ) => {
    setSelectedTaxonomy(taxonomy);
    setEditingTerm(term);
    setShowTermModal(true);
  };

  const handleDeleteTerm = async (termId: string) => {
    if (!confirm('Are you sure you want to delete this term?')) {
      return;
    }

    try {
      await deleteTerm(termId);
      toast.success('Term deleted successfully');
    } catch (error) {
      toast.error('Failed to delete term');
    }
  };

  const handleSaveTerm = async (data: CreateTermDto) => {
    try {
      if (editingTerm) {
        await updateTerm(editingTerm.id, data);
        toast.success('Term updated successfully');
      } else {
        await createTerm(data);
        toast.success('Term created successfully');
      }
      setShowTermModal(false);
      setEditingTerm(null);
      setSelectedTaxonomy(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      toast.error(
        `Failed to ${editingTerm ? 'update' : 'create'} term: ${errorMessage}`
      );
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading taxonomies...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Tags & Categories</h1>
            <p className={styles.subtitle}>
              Organize your content with categories (like Services, Industries,
              Technologies) and their associated tags.
            </p>
            <div className={styles.helpText}>
              <Icon name="info" size="sm" />
              <span>
                <strong>Category</strong> = Type of classification (e.g.,
                Services) • <strong>Tag</strong> = Individual item (e.g., Web
                Development)
              </span>
            </div>
          </div>
          <Button onClick={handleCreateTaxonomy} variant="primary">
            <Icon name="plus" size="sm" />
            New Category
          </Button>
        </div>

        {/* Taxonomies List */}
        <div className={styles.taxonomiesList}>
          {taxonomies.length === 0 ? (
            <div className={styles.empty}>
              <Icon name="folder" size="lg" />
              <h3>No categories yet</h3>
              <p>
                Create your first category (e.g., Services, Industries) to start
                organizing your tags.
              </p>
              <Button onClick={handleCreateTaxonomy} variant="primary">
                Create Category
              </Button>
            </div>
          ) : (
            taxonomies.map((taxonomy) => (
              <TaxonomyCard
                key={taxonomy.id}
                taxonomy={taxonomy}
                onEditTaxonomy={handleEditTaxonomy}
                onDeleteTaxonomy={handleDeleteTaxonomy}
                onCreateTerm={handleCreateTerm}
                onEditTerm={handleEditTerm}
                onDeleteTerm={handleDeleteTerm}
              />
            ))
          )}
        </div>

        {/* Modals */}
        {showTaxonomyModal && (
          <TaxonomyModal
            taxonomy={editingTaxonomy}
            onSave={handleSaveTaxonomy}
            onClose={() => {
              setShowTaxonomyModal(false);
              setEditingTaxonomy(null);
            }}
          />
        )}

        {showTermModal && selectedTaxonomy && (
          <TermModal
            term={editingTerm}
            taxonomyId={selectedTaxonomy.id}
            taxonomyName={selectedTaxonomy.name}
            onSave={handleSaveTerm}
            onClose={() => {
              setShowTermModal(false);
              setEditingTerm(null);
              setSelectedTaxonomy(null);
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Taxonomy Modal Component
interface TaxonomyModalProps {
  taxonomy: TaxonomyWithTerms | null;
  onSave: (data: CreateTaxonomyDto) => void;
  onClose: () => void;
}

const TaxonomyModal: React.FC<TaxonomyModalProps> = ({
  taxonomy,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<CreateTaxonomyDto>({
    name: taxonomy?.name || '',
    slug: taxonomy?.slug || '',
    description: taxonomy?.description || '',
    isActive: taxonomy?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{taxonomy ? 'Edit Category' : 'Create Category'}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <Icon name="x" size="sm" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formField}>
            <label>Category Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Services, Industries, Technologies"
              required
            />
          </div>

          <div className={styles.formField}>
            <label>Slug *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value })
              }
              placeholder="e.g., services, industries"
              required
            />
            <small>Used in URLs and API calls</small>
          </div>

          <div className={styles.formField}>
            <label>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Brief description of this category"
              rows={3}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              <span>Active</span>
            </label>
          </div>

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose} variant="ghost">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {taxonomy ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Term Modal Component
interface TermModalProps {
  term: TermWithTaxonomy | null;
  taxonomyId: string;
  taxonomyName: string;
  onSave: (data: CreateTermDto) => void;
  onClose: () => void;
}

// Helper to convert string to kebab-case slug
const toSlug = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

const TermModal: React.FC<TermModalProps> = ({
  term,
  taxonomyId,
  taxonomyName,
  onSave,
  onClose,
}) => {
  const [formData, setFormData] = useState<CreateTermDto>({
    taxonomyId: term?.taxonomyId || taxonomyId,
    nameEN: term?.nameEN || '',
    nameFR: term?.nameFR || '',
    slug: term?.slug || '',
    description: term?.description || '',
    icon: term?.icon || '',
    color: term?.color || '#3B82F6',
    isActive: term?.isActive ?? true,
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(!!term?.slug);

  // Auto-generate slug from English name if not manually edited
  const handleNameENChange = (value: string) => {
    const newData: CreateTermDto = { ...formData, nameEN: value };
    if (!slugManuallyEdited) {
      newData.slug = toSlug(value);
    }
    setFormData(newData);
  };

  // Mark slug as manually edited when user types in it
  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    setFormData({ ...formData, slug: toSlug(value) });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2>{term ? 'Edit Tag' : 'Create Tag'}</h2>
            <p className={styles.modalSubtitle}>in {taxonomyName} category</p>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <Icon name="x" size="sm" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Tag Name (English) *</label>
              <input
                type="text"
                value={formData.nameEN}
                onChange={(e) => handleNameENChange(e.target.value)}
                placeholder="e.g., Web Development"
                required
              />
            </div>

            <div className={styles.formField}>
              <label>Tag Name (French) *</label>
              <input
                type="text"
                value={formData.nameFR || ''}
                onChange={(e) =>
                  setFormData({ ...formData, nameFR: e.target.value })
                }
                placeholder="e.g., Développement Web"
                required
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label>Slug *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="e.g., web-development"
              required
            />
            <small>
              Auto-generated from name. Lowercase letters, numbers, and hyphens
              only.
            </small>
          </div>

          <div className={styles.formField}>
            <label>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Brief description"
              rows={2}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label>Icon</label>
              <input
                type="text"
                value={formData.icon || ''}
                onChange={(e) =>
                  setFormData({ ...formData, icon: e.target.value })
                }
                placeholder="e.g., code, palette, chart"
              />
              <small>Icon name from your icon library</small>
            </div>

            <div className={styles.formField}>
              <label>Color</label>
              <input
                type="color"
                value={formData.color || '#3B82F6'}
                onChange={(e) =>
                  setFormData({ ...formData, color: e.target.value })
                }
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
              <span>Active</span>
            </label>
          </div>

          <div className={styles.modalActions}>
            <Button type="button" onClick={onClose} variant="ghost">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {term ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
